import { Link as RouterLink } from 'react-router-dom'

import {
  AvatarStack,
  BlankSlate,
  Box,
  Button,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { usePerformanceGoalsMetricGroupDataTable } from 'generated/resources/datatable.graphql'
import { PerformanceGoalsUpdateMetricGroupFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { CycleTag } from 'modules/performance_cycles/components/CycleTag'
import { ProgressSummary } from 'modules/performance_goals/components/ProgressSummary'
import { PublicTag } from 'modules/performance_goals/components/PublicTag'
import { UpdateMetricGroupModal } from 'modules/performance_goals/components/UpdateMetricGroupModal'

const Actions: React.FC<{
  canUpdateMetricGroup: boolean
  metricGroup: PerformanceGoalsUpdateMetricGroupFragment
}> = ({ metricGroup, canUpdateMetricGroup }) => {
  const title = metricGroup.title
  const { navigation } = useNavigation()
  const [showEditGoalModal, toggleUpdateGoalModal] = useToggle()

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
      {canUpdateMetricGroup && (
        <Box flexDirection="row" justifyContent="flexEnd" alignItems="center" gap="s8">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="start">
              <PopoverMenu.TouchableOption
                title={i18n.t('performance_goals.pages.metric_group_page.edit')}
                onClick={toggleUpdateGoalModal(true)}
                icon="pencil"
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      )}
      <RouterLink
        to={navigation.performanceGoals.groups.group.fullPath({
          id: metricGroup.id.toString(),
        })}
      >
        <Button
          type="secondary"
          aria-label={i18n.t('performance_goals.goal_list.navigate', { title })}
          iconName="arrow-right"
        />
      </RouterLink>
      {showEditGoalModal && (
        <UpdateMetricGroupModal metricGroup={metricGroup} onClose={toggleUpdateGoalModal(false)} />
      )}
    </Box>
  )
}

export const GoalsTable: React.FC<{
  canCreateMetricGroup: boolean
  search?: string
  cycleIds?: string[]
  noCycleSelected?: boolean
  onCreate: () => void
}> = ({ canCreateMetricGroup, search, cycleIds, noCycleSelected, onCreate }) => {
  const source = usePerformanceGoalsMetricGroupDataTable(
    [
      {
        identifier: 'title',
        kind: 'path',
        title: i18n.t('performance_goals.goal_list.columns.title'),
        path: 'title',
        width: 400,
      },
      {
        identifier: 'assignees',
        kind: 'custom',
        title: i18n.t('performance_goals.goal_list.columns.assignees'),
        render: ({ assigneesConnection }) => {
          const assignees = assigneesConnection.edges

          if (!assignees.length) {
            return <Text.Normal>-</Text.Normal>
          }

          const avatarStack = assignees.map(({ node: assignee }) => ({
            label: assignee.fullName || '',
            url: buildUrl(assignee.access.avatar),
          }))

          return (
            <AvatarStack
              avatars={avatarStack}
              leftover={(assigneesConnection?.totalCount || 0) - assignees.length}
              size="s32"
              textColor="grey400"
              textSize="200"
              backgroundColor="grey000White"
              overlap="s8"
            />
          )
        },
        template: '{{ assigneesConnection }}',
      },
      {
        identifier: 'cycle',
        kind: 'custom',
        title: i18n.t('performance_cycles.cycle_filter.label'),
        render: ({ performanceCycles }) => {
          const cycle = performanceCycles[0]

          return cycle ? (
            <CycleTag title={cycle.title} startOn={cycle.startOn} endOn={cycle.endOn} />
          ) : (
            <Text.Normal>-</Text.Normal>
          )
        },
        template: '{{ cycle }}',
      },

      {
        identifier: 'public',
        kind: 'custom' as const,
        title: i18n.t('performance_goals.goal_list.columns.public'),
        render: ({ public: isPublic }: { public: boolean }) => {
          return <PublicTag isPublic={isPublic} />
        },
        template: '{{ public }}',
      },

      {
        identifier: 'metrics',
        kind: 'custom',
        title: i18n.t('performance_goals.employee_list.columns.progress'),
        render: ({ summary }) => {
          if (summary) {
            return (
              <ProgressSummary
                progress={summary.averageProgress}
                needsAttention={summary.needsAttention}
              />
            )
          }
        },
        template: '{{ summary_averageProgress }}',
      },
      {
        identifier: 'actions',
        kind: 'decorative',
        fixed: true,
        width: 120,
        render: (metricGroup) => {
          return (
            <Actions
              metricGroup={metricGroup}
              canUpdateMetricGroup={metricGroup._policies_update_can ?? false}
            />
          )
        },
      },
    ],
    {
      variables: { cycleIds, noCycle: noCycleSelected, search },
    }
  )

  return (
    <Box gap="s16">
      <DataTable
        source={source}
        exportFileName={i18n.t('performance_goals.goal_list.export_filename')}
        disableEditColumns
        disableExport
        emptyState={
          <BlankSlate
            illustration="analytics-graph-lines"
            size="small"
            title={i18n.t('performance_goals.goal_list.blank_slate.title')}
            description={i18n.t('performance_goals.goal_list.blank_slate.description')}
          >
            {canCreateMetricGroup && (
              <Button iconName="add" onClick={onCreate}>
                {i18n.t('performance_goals.goal_list.create_button')}
              </Button>
            )}
          </BlankSlate>
        }
      />
    </Box>
  )
}
